import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { CustomErrorToast } from '@shared/components/customErrorToast';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { SerializedError } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';
import React from 'react';

interface ErrorData {
  message?: string;
  statusCode?: number;
  error?: string;
}

interface ShowErrorOptions {
  title?: string;
  message?: React.ReactNode;
  error?: unknown;
  onRetry?: () => void;
  retryLabel?: string;
  suggestion?: string;
  technical?: boolean;
  duration?: number;
}

interface ErrorContext {
  operation: string;
  defaultMessage: string;
  suggestions?: Record<string, string>;
}

const HTTP_STATUS_SUGGESTIONS: Record<number, string> = {
  400: 'Please check your input and try again',
  401: 'Your session may have expired. Try logging in again',
  403: 'You do not have permission to perform this action',
  404: 'The requested resource was not found',
  413: 'The file is too large. Please use a smaller file',
  429: 'Too many requests. Please wait a moment and try again',
  500: 'Server error. Our team has been notified',
  502: 'Service temporarily unavailable. Please try again',
  503: 'Service is under maintenance. Please try again later',
  504: 'Request timeout. Please check your connection',
};

export const useErrorHandler = () => {
  const { t } = useTranslation();

  const extractErrorMessage = useCallback(
    (error: unknown): { message: string; statusCode?: number; technical: boolean } => {
      // Handle RTK Query errors
      if (error && typeof error === 'object') {
        if ('data' in error) {
          const fetchError = error as FetchBaseQueryError;
          if (fetchError.data && typeof fetchError.data === 'object') {
            const errorData = fetchError.data as ErrorData;
            return {
              message: errorData.message || errorData.error || t('common.errorMessage'),
              statusCode: errorData.statusCode || (fetchError.status as number),
              technical: !errorData.message,
            };
          }
        }
        if ('message' in error) {
          const serializedError = error as SerializedError;
          return {
            message: serializedError.message || t('common.errorMessage'),
            technical: false,
          };
        }
        if ('status' in error) {
          const fetchError = error as FetchBaseQueryError;
          if (fetchError.status === 'FETCH_ERROR') {
            return {
              message: t('common.networkError'),
              technical: false,
            };
          }
          if (typeof fetchError.status === 'number') {
            return {
              message:
                HTTP_STATUS_SUGGESTIONS[fetchError.status] ||
                `Request failed with status ${fetchError.status}`,
              statusCode: fetchError.status,
              technical: false,
            };
          }
        }
      }

      // Handle standard Error objects
      if (error instanceof Error) {
        return {
          message: error.message,
          technical: false,
        };
      }

      // Handle string errors
      if (typeof error === 'string') {
        return {
          message: error,
          technical: false,
        };
      }

      return {
        message: t('common.errorMessage'),
        technical: true,
      };
    },
    [t],
  );

  const getSuggestion = useCallback((statusCode?: number, context?: string): string | undefined => {
    if (statusCode && HTTP_STATUS_SUGGESTIONS[statusCode]) {
      return HTTP_STATUS_SUGGESTIONS[statusCode];
    }

    if (context) {
      const contextSuggestions: Record<string, string> = {
        upload: 'Ensure the file meets the requirements and try again',
        delete: 'Refresh the page and try again',
        save: 'Check your changes and try again',
        fetch: 'Refresh the page to reload the data',
        network: 'Check your internet connection',
      };
      return contextSuggestions[context];
    }

    return undefined;
  }, []);

  const showError = useCallback(
    (options: ShowErrorOptions) => {
      const {
        title,
        message: providedMessage,
        error,
        onRetry,
        retryLabel,
        suggestion: providedSuggestion,
        technical: providedTechnical,
        duration = 5000,
      } = options;

      let finalMessage = providedMessage;
      let finalSuggestion = providedSuggestion;
      let finalTechnical = providedTechnical || false;
      let statusCode: number | undefined;

      if (error && !providedMessage) {
        const extracted = extractErrorMessage(error);
        finalMessage = extracted.message;
        statusCode = extracted.statusCode;
        finalTechnical = extracted.technical;
      }

      if (!finalSuggestion && statusCode) {
        finalSuggestion = getSuggestion(statusCode);
      }

      toast.error(
        (props) => (
          <CustomErrorToast
            {...props}
            title={title}
            message={finalMessage}
            onRetry={onRetry}
            retryLabel={retryLabel}
            suggestion={finalSuggestion}
            technical={finalTechnical}
          />
        ),
        {
          closeButton: false,
          autoClose: duration,
          icon: false,
        },
      );
    },
    [extractErrorMessage, getSuggestion],
  );

  const showErrorWithContext = useCallback(
    (context: ErrorContext, error: unknown, onRetry?: () => void) => {
      const extracted = extractErrorMessage(error);
      const suggestion =
        context.suggestions?.[String(extracted.statusCode)] ||
        getSuggestion(extracted.statusCode, context.operation);

      showError({
        title: `${context.operation} failed`,
        message: extracted.message || context.defaultMessage,
        onRetry,
        suggestion,
        technical: extracted.technical,
      });
    },
    [extractErrorMessage, getSuggestion, showError],
  );

  return {
    showError,
    showErrorWithContext,
    extractErrorMessage,
  };
};
